import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { useModalConfirm } from "@ryde/redux";
import {
  Api,
  companyIdSelector,
  ConfirmResult,
  dateISOApplyTimezone,
  getCoursesCalendarSlotsPath,
  ICourseDto,
  ICoursesCalendarSlotDto,
  ICoursesCalendarSlotsResponse,
  Log,
  nowTDateISO,
  SimpleResult,
  TimeZoneEnum,
  toDatesUnitNormalized,
  useDrawers,
  useTimezone,
} from "@ryde/shared";

import {
  CourseProduceDateRangeDialogContent,
  ICourseProduceDateRange,
} from "./course-produce-date-range-dialog-content";

/** Today + tomorrow inclusive (two calendar days). */
const DEFAULT_RANGE_DAYS = 1;

export const useCourseProduceByDateRange = (): {
  openProduceByDateRange: (course: ICourseDto) => Promise<void>;
} => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { confirmDialogResult } = useModalConfirm();
  const { openCoursesCalendarRideProduceDrawer } = useDrawers();
  const companyId = useRecoilValue(companyIdSelector);
  const rangeRef = useRef<ICourseProduceDateRange | null>(null);

  const handleRangeChange = useCallback((range: ICourseProduceDateRange) => {
    rangeRef.current = range;
  }, []);

  const openProduceByDateRange = useCallback(
    async (course: ICourseDto): Promise<void> => {
      const courseId = course.id;
      if (!companyId || !courseId) {
        return;
      }

      const today = nowTDateISO(timezone);
      const defaultTo = convertDaysAheadEndOfDay(
        today,
        DEFAULT_RANGE_DAYS,
        timezone,
      );
      const initialRange = toDatesUnitNormalized({
        dt1: today,
        dt2: defaultTo,
        timezone,
      });
      rangeRef.current = {
        dateFrom: initialRange.date1,
        dateTo: initialRange.date2,
      };

      const confirmationResult = await confirmDialogResult({
        title: t("course.PRODUCE_RIDES_DATE_RANGE_TITLE"),
        customContent: (
          <CourseProduceDateRangeDialogContent
            course={course}
            initialFrom={initialRange.date1}
            initialTo={initialRange.date2}
            onChange={handleRangeChange}
          />
        ),
      });

      if (confirmationResult !== ConfirmResult.OK || !rangeRef.current) {
        return;
      }

      const { dateFrom, dateTo } = rangeRef.current;
      const normalized = toDatesUnitNormalized({
        dt1: dateFrom,
        dt2: dateTo,
        timezone,
      });

      try {
        const path = getCoursesCalendarSlotsPath(companyId);
        const res = await Api.post<SimpleResult<ICoursesCalendarSlotsResponse>>(
          path,
          {
            dateFrom: normalized.date1,
            dateTo: normalized.date2,
            rideStatusFilter: "all",
            filterCourseIds: [courseId],
          },
        );

        if (!res?.data?.success || !res.data.data) {
          Log.warnAndToast(t("course.calendar.producePreviewError"));
          return;
        }

        const slots: ICoursesCalendarSlotDto[] = res.data.data.slots ?? [];
        if (!slots.length) {
          Log.warnAndToast(t("course.PRODUCE_RIDES_NO_SLOTS_IN_RANGE"));
          return;
        }

        await openCoursesCalendarRideProduceDrawer({
          slots,
          shouldUseSharedProduceFlow: true,
        });
      } catch (error: unknown) {
        Log.errorAndMessageToast({
          message: t("course.calendar.producePreviewError"),
          error: error instanceof Error ? error : new Error(String(error)),
        });
      }
    },
    [
      companyId,
      confirmDialogResult,
      handleRangeChange,
      openCoursesCalendarRideProduceDrawer,
      t,
      timezone,
    ],
  );

  return { openProduceByDateRange };
};

const convertDaysAheadEndOfDay = (
  from: TDateISO,
  daysAhead: number,
  timezone: TimeZoneEnum,
): TDateISO =>
  dateISOApplyTimezone(from, timezone)
    .startOf("day")
    .add(daysAhead, "day")
    .endOf("day")
    .utc()
    .toISOString() as TDateISO;
